/**
 * HostPoint POS - Network Status Components
 * UI components for displaying network and sync status
 */

import { useState } from 'react';
import { Wifi, WifiOff, RefreshCw, Download, Check, AlertTriangle, Cloud, CloudOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useNetworkStatus, useInstallPrompt, useSyncStatus, useServiceWorker } from '@/lib/pwa-hooks';
import { cn } from '@/lib/utils';

/**
 * Compact network status indicator for POS header
 */
export function NetworkIndicator({ className }) {
  const { isOnline } = useNetworkStatus();
  const { syncStatus } = useSyncStatus();
  
  const hasPending = syncStatus.pendingOrders > 0 || syncStatus.pendingPayments > 0;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn(
            "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors",
            isOnline 
              ? hasPending 
                ? "bg-yellow-500/10 text-yellow-500" 
                : "bg-emerald-500/10 text-emerald-500"
              : "bg-red-500/10 text-red-500",
            className
          )}>
            {isOnline ? (
              hasPending ? (
                <>
                  <Cloud className="h-3.5 w-3.5" />
                  <span>{syncStatus.pendingOrders}</span>
                </>
              ) : (
                <>
                  <Wifi className="h-3.5 w-3.5" />
                  <span>Online</span>
                </>
              )
            ) : (
              <>
                <WifiOff className="h-3.5 w-3.5" />
                <span>Offline</span>
              </>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {isOnline ? (
            hasPending ? (
              <p>{syncStatus.pendingOrders} orders pending sync</p>
            ) : (
              <p>Connected - All data synced</p>
            )
          ) : (
            <p>Offline - Orders saved locally</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Detailed sync status panel
 */
export function SyncStatusPanel({ apiUrl, token, onClose }) {
  const { isOnline, triggerSync } = useNetworkStatus();
  const { syncStatus, isSyncing, manualSync, refreshStatus } = useSyncStatus();
  const [syncResult, setSyncResult] = useState(null);

  const handleManualSync = async () => {
    const result = await manualSync(apiUrl, token);
    setSyncResult(result);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isOnline ? (
              <Cloud className="h-5 w-5 text-emerald-500" />
            ) : (
              <CloudOff className="h-5 w-5 text-red-500" />
            )}
            Sync Status
          </DialogTitle>
          <DialogDescription>
            {isOnline 
              ? "Your device is connected. Data will sync automatically."
              : "You're offline. Data will sync when connection is restored."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Status Overview */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold">{syncStatus.pendingOrders}</div>
              <div className="text-xs text-muted-foreground">Pending Orders</div>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold">{syncStatus.pendingPayments}</div>
              <div className="text-xs text-muted-foreground">Pending Payments</div>
            </div>
          </div>

          {/* Failed Items Warning */}
          {syncStatus.failedItems > 0 && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">{syncStatus.failedItems} items failed to sync</span>
            </div>
          )}

          {/* Last Sync */}
          {syncStatus.lastSyncAttempt && (
            <div className="text-xs text-muted-foreground">
              Last sync attempt: {new Date(syncStatus.lastSyncAttempt).toLocaleString()}
            </div>
          )}

          {/* Sync Result */}
          {syncResult && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-emerald-500 mb-2">
                <Check className="h-4 w-4" />
                <span className="font-medium">Sync Complete</span>
              </div>
              <div className="text-xs text-muted-foreground">
                Orders: {syncResult.orders.synced} synced, {syncResult.orders.failed} failed
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={refreshStatus}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={handleManualSync} 
            disabled={!isOnline || isSyncing || syncStatus.pendingOrders === 0}
          >
            {isSyncing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <Cloud className="h-4 w-4 mr-2" />
                Sync Now
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Install App Banner
 */
export function InstallBanner({ onDismiss }) {
  const { isInstallable, isInstalled, promptInstall } = useInstallPrompt();
  
  // Initialize dismissed state from localStorage
  const [dismissed, setDismissed] = useState(() => {
    const wasDismissed = localStorage.getItem('pwa_install_dismissed');
    if (wasDismissed) {
      const dismissedAt = new Date(wasDismissed);
      const daysSinceDismissed = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
      // Show again after 7 days
      return daysSinceDismissed < 7;
    }
    return false;
  });

  if (!isInstallable || isInstalled || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa_install_dismissed', new Date().toISOString());
    onDismiss?.();
  };

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      handleDismiss();
    }
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white p-4 rounded-xl shadow-lg z-50 animate-in slide-in-from-bottom-5">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-white/20 rounded-lg">
          <Download className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold">Install HostPoint POS</h3>
          <p className="text-sm text-white/80 mt-0.5">
            Install our app for faster access and offline support
          </p>
          <div className="flex gap-2 mt-3">
            <Button 
              size="sm" 
              variant="secondary" 
              className="bg-white text-emerald-600 hover:bg-white/90"
              onClick={handleInstall}
            >
              Install Now
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-white hover:bg-white/20"
              onClick={handleDismiss}
            >
              Not Now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Update Available Banner
 */
export function UpdateBanner() {
  const { updateAvailable, updateApp } = useServiceWorker();

  if (!updateAvailable) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bg-blue-600 text-white p-3 text-center z-50">
      <div className="flex items-center justify-center gap-4">
        <span className="text-sm">A new version is available!</span>
        <Button 
          size="sm" 
          variant="secondary"
          className="bg-white text-blue-600 hover:bg-white/90"
          onClick={updateApp}
        >
          Update Now
        </Button>
      </div>
    </div>
  );
}

/**
 * Offline Mode Banner
 */
export function OfflineBanner() {
  const { isOnline } = useNetworkStatus();

  if (isOnline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-yellow-900 p-2 text-center z-50">
      <div className="flex items-center justify-center gap-2 text-sm font-medium">
        <WifiOff className="h-4 w-4" />
        <span>You&apos;re offline. Orders will sync when connection is restored.</span>
      </div>
    </div>
  );
}

export default {
  NetworkIndicator,
  SyncStatusPanel,
  InstallBanner,
  UpdateBanner,
  OfflineBanner
};
